import { CheckCircle2, Globe, Image, LayoutDashboard, Rocket, Sparkles, Wand2 } from "lucide-react";

export default function ReviewStep({ form, projectId, status, liveUrl }) {
    const pages = Array.isArray(form.pages) ? form.pages : [];

    const launchItems = [
        {
            icon: <Globe size={18} />,
            title: "Multi-page website",
            text: `${pages.length || 4} recommended pages prepared for this business.`
        },
        {
            icon: <LayoutDashboard size={18} />,
            title: "CRM-ready modules",
            text: "Generated content can be edited later from your CRM workspace."
        },
        {
            icon: <Image size={18} />,
            title: "Smart image plan",
            text: "Nexus can use seed images, stock sources, and AI fallback where needed."
        },
        {
            icon: <Wand2 size={18} />,
            title: "AI section studio",
            text: "After launch, you can preview AI section variants before redeploying."
        }
    ];

    return (
        <div className="wg-review-step premium">
            <div className="wg-launch-hero">
                <div>
                    <div className="wg-launch-kicker">
                        <Sparkles size={16} />
                        <span>Ready to generate</span>
                    </div>

                    <h2>Launch an AI-built website system, not just a page.</h2>

                    <p>
                        Nexus will generate your website, prepare CRM-editable content,
                        create a preview session, and make it ready for publishing,
                        variants, image intelligence, and future optimization.
                    </p>
                </div>

                <div className="wg-launch-status">
                    <span>Status</span>
                    <strong>{status || "Not generated yet"}</strong>
                    {projectId ? <small>Project: {projectId}</small> : <small>Guest preview will be created first</small>}
                </div>
            </div>

            <div className="wg-launch-grid">
                {launchItems.map((item) => (
                    <div className="wg-launch-card" key={item.title}>
                        <div className="wg-launch-icon">{item.icon}</div>
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                    </div>
                ))}
            </div>

            <div className="wg-review-group premium">
                <h3 className="wg-review-group-title">Business Brief</h3>

                <div className="wg-review-grid">
                    <ReviewItem label="Business" value={form.businessName} />
                    <ReviewItem label="Industry" value={form.industry} />
                    <ReviewItem label="Sub Industry" value={form.subIndustry} />
                    <ReviewItem label="Goal" value={form.goalOfWebsite} />
                    <ReviewItem label="Tone" value={form.tone} />
                    <ReviewItem label="Style" value={[form.themeKey, form.brandStyle, form.pricePositioning].filter(Boolean).join(" • ")} />
                </div>
            </div>

            <div className="wg-review-group premium">
                <h3 className="wg-review-group-title">AI Strategy</h3>

                <div className="wg-review-grid">
                    <ReviewItem label="Business Model" value={form.businessModel} />
                    <ReviewItem label="Website Mode" value={form.primaryWebsiteMode} />
                    <ReviewItem label="Main Entity" value={form.primaryEntityType} />
                    <ReviewItem label="Location Focus" value={form.locationFocus} />
                </div>
            </div>

            <div className="wg-review-group premium">
                <h3 className="wg-review-group-title">Recommended Pages</h3>

                <div className="wg-review-tags">
                    {pages.length > 0 ? (
                        pages.map((page) => (
                            <span key={page} className="wg-review-tag">
                                <CheckCircle2 size={14} />
                                {page}
                            </span>
                        ))
                    ) : (
                        <span className="wg-review-empty">AI will decide pages</span>
                    )}
                </div>
            </div>

            <div className="wg-review-group premium">
                <h3 className="wg-review-group-title">Business Description</h3>
                <div className="wg-review-item wg-review-item-full">
                    <span>{form.description || "Add a short description to improve AI quality."}</span>
                </div>
            </div>

            {liveUrl && (
                <div className="wg-review-group premium">
                    <h3 className="wg-review-group-title">Live Website</h3>
                    <a href={liveUrl} target="_blank" rel="noreferrer" className="wg-live-link">
                        <Rocket size={16} />
                        Open Live Site
                    </a>
                </div>
            )}
        </div>
    );
}

function ReviewItem({ label, value }) {
    return (
        <div className="wg-review-item">
            <strong>{label}</strong>
            <span>{value || "-"}</span>
        </div>
    );
}